import { DrmProvider } from "@core-services/data-types"
import { DrmConfig, UpsertDrm } from "@core-services/portal"
import { zodResolver } from "@hookform/resolvers/zod"
import { IconPaperclip } from "@tabler/icons-react"
import {
  FormControlLabel,
  Light,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  TextField,
} from "@vindral/components"
import { useEffect, useState } from "react"
import { useController, useForm } from "react-hook-form"
import { z } from "zod"
import { FormSection } from "../../../atoms/FormSection"
import { SaveButton } from "../../../atoms/SaveButton"
import { SaveSection } from "../../../atoms/SaveSection"

const DEFAULT_CPIX_URL = "https://cpix.ezdrm.com/KeyGenerator/cpix2.aspx"

const resolver = zodResolver(UpsertDrm)
export type DrmSchema = z.infer<typeof UpsertDrm>

interface OrganizationEditDrmProps {
  config?: DrmConfig
  organizationPublicId: string
  onUpdate: (value: DrmSchema) => Promise<void>
  canEdit: boolean
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => {
      if (typeof reader.result === "string") {
        const result = reader.result
        const base64 = result.replace(/^data:.*;base64,/, "")
        resolve(base64)
      } else {
        reject(new Error("FileReader result is not a string"))
      }
    }
    reader.onerror = (error) => reject(error)
  })
}

function values(organizationPublicId: string, config?: DrmConfig) {
  return {
    organizationPublicId,
    provider: config?.provider ?? DrmProvider.EzDrm,
    token: config?.token ?? "",
    cpixUrl: config?.cpixUrl ?? DEFAULT_CPIX_URL,
    widevineLicenseUrl: config?.widevineLicenseUrl ?? "",
    playreadyLicenseUrl: config?.playreadyLicenseUrl ?? "",
    fairplayLicenseUrl: config?.fairplayLicenseUrl ?? "",
    fairplayCertificate: undefined,
  }
}

export function OrganizationEditDrm(props: OrganizationEditDrmProps) {
  const { config, organizationPublicId, onUpdate, canEdit } = props
  const [filename, setFilename] = useState<string | undefined>(undefined)

  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    setError,
    formState: { errors, isDirty, isValid, isSubmitSuccessful },
  } = useForm({
    resolver,
    mode: "onChange",
    defaultValues: values(organizationPublicId, config),
  })

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset({}, { keepValues: true })
      setFilename(undefined)
    }
  }, [isSubmitSuccessful, reset])

  useEffect(() => {
    reset(values(organizationPublicId, config))
  }, [config, organizationPublicId, reset])

  const cpixUrl = watch("cpixUrl")

  const onSubmit = async (value: DrmSchema) => {
    await onUpdate(value)
  }

  const providerController = useController({ control, name: "provider" })
  const certificateController = useController({ control, name: "fairplayCertificate" })

  const cannotEdit = !canEdit
  const columnWidth = "w-full max-w-lg"

  return (
    <form className="max-w-screen-xl space-y-4 py-4" onSubmit={handleSubmit(onSubmit)}>
      <FormSection width={columnWidth} title="Provider" description="Choose your DRM provider">
        <Select
          disabled={cannotEdit}
          value={providerController.field.value}
          onValueChange={(value) => {
            providerController.field.onChange(value)
            if (!cpixUrl && value === DrmProvider.EzDrm) {
              setValue("cpixUrl", DEFAULT_CPIX_URL, {
                shouldDirty: true,
                shouldValidate: true,
              })
            }
          }}
        >
          <SelectTrigger size="medium">
            <SelectValue placeholder="Select provider" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={DrmProvider.EzDrm}>EZDRM</SelectItem>
            <SelectItem value={DrmProvider.Irdeto}>irdeto</SelectItem>
          </SelectContent>
        </Select>
      </FormSection>

      <FormSection width={columnWidth} title="Token" description="Authentication token">
        <TextField disabled={cannotEdit} {...register("token")} error={!!errors?.token?.message} placeholder="" />
        <FormControlLabel error={errors?.token?.message} />
      </FormSection>

      <FormSection width={columnWidth} title="Cpix url" description="The CPIX request URL">
        <TextField disabled={cannotEdit} {...register("cpixUrl")} error={!!errors?.cpixUrl?.message} placeholder="" />
        <FormControlLabel error={errors?.cpixUrl?.message} />
      </FormSection>

      <FormSection width={columnWidth} title="Widevine license url" description="">
        <TextField
          disabled={cannotEdit}
          {...register("widevineLicenseUrl")}
          error={!!errors?.widevineLicenseUrl?.message}
          placeholder=""
        />
        <FormControlLabel error={errors?.widevineLicenseUrl?.message} />
      </FormSection>

      <FormSection width={columnWidth} title="Playready license url" description="">
        <TextField
          disabled={cannotEdit}
          {...register("playreadyLicenseUrl")}
          error={!!errors?.playreadyLicenseUrl?.message}
          placeholder=""
        />
        <FormControlLabel error={errors?.playreadyLicenseUrl?.message} />
      </FormSection>

      <FormSection width={columnWidth} title="Fairplay license url" description="">
        <TextField
          disabled={cannotEdit}
          {...register("fairplayLicenseUrl")}
          error={!!errors?.fairplayLicenseUrl?.message}
          placeholder=""
        />
        <FormControlLabel error={errors?.fairplayLicenseUrl?.message} />
      </FormSection>

      <FormSection width={columnWidth} title="Fairplay certificate" description="">
        <div className="flex">
          <div className="flex flex-col gap-1">
            {errors.fairplayCertificate?.message && (
              <div className="text-fg-danger-subtle transition-colors">{errors.fairplayCertificate.message}</div>
            )}
            <label
              htmlFor="file_input"
              className="-m-1 flex w-fit cursor-pointer items-center gap-1 p-1 text-fg-subtle"
            >
              <IconPaperclip className="h-3.5 w-3.5" />
              Upload Fairplay certificate
            </label>
            <input
              className="hidden"
              id="file_input"
              type="file"
              name="attachment"
              accept=".cer"
              onChange={async (e) => {
                try {
                  if (e.target.files?.[0]) {
                    const file = e.target.files[0]
                    const base64 = await fileToBase64(file)
                    certificateController.field.onChange(base64)
                    setFilename(file.name)
                  }
                } catch (error) {
                  setError("fairplayCertificate", { message: "Failed to parse file" })
                }
              }}
            />
            {filename ? <span className="line-clamp-2 text-sm text-fg-subtle">{filename}</span> : null}
          </div>
          <div className="ml-auto flex items-center gap-2 text-fg-subtle">
            {config?.fairplayCertificateUploaded ? "Uploaded" : ""}
            <Light color={config?.fairplayCertificateUploaded && !filename ? "green" : "red"} />
          </div>
        </div>
      </FormSection>
      {canEdit && (
        <SaveSection width={columnWidth}>
          <SaveButton text="Save" disabled={!isDirty || !isValid} />
        </SaveSection>
      )}
    </form>
  )
}
